import {useQuery} from '@tanstack/react-query';
import {getBackendUrl} from 'utilities';

import type {AutodraftAppKey} from './autodraftApps';

export type DashConnectionStatus = 'connected' | 'needs_reauth' | 'unknown';

type JanusConnection = {
  connection_id: string;
  connector_id: number;
  status: {'.tag': string};
};

type JanusResponse = {
  safe_connections: JanusConnection[];
};

// Admin-managed connectors — connected for all users without action.
// Paper is indexed by the Dropbox connector.
const ADMIN_MANAGED_APPS: AutodraftAppKey[] = [
  'jira',
  'confluence',
  'google_drive',
  'dropbox',
  'dropboxos',
  'paper',
];

// Janus connector_id → our app key (only user-managed connectors appear here).
const JANUS_ID_TO_APP: Record<number, AutodraftAppKey> = {
  22: 'slack',
  24: 'airtable',
};

async function fetchDashConnections(): Promise<Map<AutodraftAppKey, DashConnectionStatus>> {
  const map = new Map<AutodraftAppKey, DashConnectionStatus>();

  // Admin-managed are always connected.
  for (const app of ADMIN_MANAGED_APPS) {
    map.set(app, 'connected');
  }

  // Fetch user-managed connection statuses from janus.
  const base = getBackendUrl().replace(/\/+$/, '');
  const res = await fetch(`${base}/api/v1/workstream/autodraft/dash-connections`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch dash connections: ${res.status}`);
  }
  const data = (await res.json()) as JanusResponse;
  for (const conn of data.safe_connections ?? []) {
    const appKey = JANUS_ID_TO_APP[conn.connector_id];
    if (appKey) {
      const tag = conn.status?.['.tag'];
      const status: DashConnectionStatus =
        tag === 'connected' ? 'connected' : tag === 'needs_reauth' ? 'needs_reauth' : 'unknown';
      map.set(appKey, status);
    }
  }

  return map;
}

export const useDashConnections = (enabled = true) =>
  useQuery({
    queryKey: ['dash-connections'],
    queryFn: fetchDashConnections,
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
